// ==================== ORGANIZATIONS PROVIDER ====================
// Multiple organizations: the customer's organizations and the one they are working in.
// The organization chosen in the header dropdown applies to every screen (undefined =
// all of them) and is remembered per user. Sub-accounts and admins never get a choice
// (no request is made).

import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { organizationsService, type Organization } from '../../services/organizations.service';
import { getStorageItem, setStorageItem, removeStorageItem } from '../../utils/helpers';
import { OrganizationsContext } from './OrganizationsContext';

const storageKey = (userId: string) => `selectedOrganization:${userId}`;

export const OrganizationsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const isCustomer = user?.role === 'CUSTOMER';

  // State
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [purchased, setPurchased] = useState(1);
  const [loading, setLoading] = useState(isCustomer);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | undefined>(() =>
    isCustomer && userId ? getStorageItem<string | null>(storageKey(userId), null) ?? undefined : undefined,
  );
  const [accountNotice, setAccountNotice] = useState<string | null>(null);
  const { pathname } = useLocation();
  // The notice belongs to the screen it was raised on (cleared before a new screen renders, so
  // that screen can raise its own)
  const [noticePathname, setNoticePathname] = useState(pathname);
  if (noticePathname !== pathname) {
    setNoticePathname(pathname);
    setAccountNotice(null);
  }

  // ==================== FETCH ORGANIZATIONS ====================

  const fetchOrganizations = useCallback(async () => {
    if (!isCustomer) return;

    try {
      setLoading(true);
      const response = await organizationsService.getAll();
      setOrganizations(response.organizations || []);
      setPurchased(response.purchased || 1);
      // A remembered organization that is no longer there means all of them
      setSelectedOrganizationId((current) =>
        current && response.organizations.some((organization) => organization.id === current) ? current : undefined,
      );
    } catch (err) {
      // Non-fatal: screens simply stay single-organization
      console.error('Failed to fetch organizations:', err);
      setSelectedOrganizationId(undefined);
    } finally {
      setLoading(false);
    }
  }, [isCustomer]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // ==================== SELECTED ORGANIZATION ====================

  const selectOrganization = useCallback(
    (organizationId: string | undefined) => {
      setSelectedOrganizationId(organizationId);
      if (organizationId) setAccountNotice(null);
      if (!userId) return;
      if (organizationId) setStorageItem(storageKey(userId), organizationId);
      else removeStorageItem(storageKey(userId));
    },
    [userId],
  );

  // ==================== CREATING NEEDS A SPECIFIC ACCOUNT ====================

  const requireAccount = useCallback(
    (action: string) => {
      if (organizations.length > 1 && !selectedOrganizationId) {
        setAccountNotice(`Please select a specific account from the dropdown at the top to ${action}.`);
        return false;
      }
      return true;
    },
    [organizations.length, selectedOrganizationId],
  );

  const dismissAccountNotice = useCallback(() => setAccountNotice(null), []);

  const value = useMemo(
    () => ({
      organizations,
      purchased,
      created: organizations.length,
      isMultiOrganization: organizations.length > 1,
      loading,
      refetch: fetchOrganizations,
      selectedOrganizationId,
      selectOrganization,
      requireAccount,
      accountNotice,
      dismissAccountNotice,
    }),
    [
      organizations,
      purchased,
      loading,
      fetchOrganizations,
      selectedOrganizationId,
      selectOrganization,
      requireAccount,
      accountNotice,
      dismissAccountNotice,
    ],
  );

  return <OrganizationsContext.Provider value={value}>{children}</OrganizationsContext.Provider>;
};
